import React, { useState } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import {
    Card, Row, Col, Typography, Select, Segmented, Spin, Tag, Space,
    Button, Flex, Empty, Image
} from "antd";
import {
    BookOutlined, UserOutlined, CalendarOutlined, GlobalOutlined,
    EnvironmentOutlined, WarningOutlined, FilterOutlined, DollarOutlined,
    ArrowRightOutlined, TeamOutlined, ClearOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios, { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PublicInvitationSummaryDto } from "../../commons/data/types";
import { GameDto } from "../../games/types";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;

export const BrowseInvitationsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Filters state
    const [onlineFilter, setOnlineFilter] = useState<string>('ALL'); // 'ALL' | 'ONLINE' | 'IN_PERSON'
    const [selectedSystemId, setSelectedSystemId] = useState<string | undefined>(undefined);

    // Fetch TTRPG games for filter
    const { data: ttrpgGames, isLoading: gamesLoading } = useQuery<GameDto[]>(
        'ttrpg-games-public',
        () => axios.get<object, AxiosResponse<GameDto[]>>('/api/launcher/games')
            .then(r => (r.data || []).filter(game => game.type?.tag === 'ttrpgs'))
    );

    // Query string construction
    const buildQueryString = () => {
        const params = new URLSearchParams();
        if (onlineFilter === 'ONLINE') {
            params.append('online', 'true');
        } else if (onlineFilter === 'IN_PERSON') {
            params.append('online', 'false');
        }
        if (selectedSystemId) {
            params.append('systemId', selectedSystemId);
        }
        const qs = params.toString();
        return qs ? `?${qs}` : '';
    };

    // Fetch public invitations
    const { data: invitations = [], isLoading: invitationsLoading } = useQuery<PublicInvitationSummaryDto[]>(
        ['public-invitations', onlineFilter, selectedSystemId],
        () => axios.get(`/api/public/invitations${buildQueryString()}`).then(res => res.data)
    );

    const handleClearFilters = () => {
        setOnlineFilter('ALL');
        setSelectedSystemId(undefined);
    };

    return (
        <LauncherLayout>
            <div style={{ maxWidth: '1200px', margin: '30px auto', padding: '0 20px' }}>
                {/* HERO HEADER */}
                <Card style={{ ...launcherCard, marginBottom: '24px' }}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap="small">
                        <div>
                            <Title level={2} style={{ margin: 0 }}>
                                {t('invitations.browse.title', 'Available Tabletop RPG Adventures')}
                            </Title>
                            <Paragraph style={{ color: '#aaa', margin: '4px 0 0 0' }}>
                                {t('invitations.browse.subtitle', 'Find ongoing and upcoming game sessions hosted by game masters and reserve your seat.')}
                            </Paragraph>
                        </div>
                    </Flex>
                </Card>

                {/* FILTER BAR */}
                <Card
                    style={{ ...launcherCard, marginBottom: '24px' }}
                    title={
                        <>
                            <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FilterOutlined /> {t('invitations.browse.filter.label', 'Filters:')}
                            </Text>
                        </>
                    }
                    children={
                        <>
                            <Flex align="center" gap="small" wrap="wrap">
                                {/* Online vs In-Person Filter */}
                                <Segmented
                                    size="small"

                                    options={[
                                        { label: t('invitations.browse.filter.all', 'All Spaces'), value: 'ALL' },
                                        { label: t('invitations.browse.filter.online', 'Online Only'), value: 'ONLINE', icon: <GlobalOutlined /> },
                                        { label: t('invitations.browse.filter.inPerson', 'In-Person'), value: 'IN_PERSON', icon: <EnvironmentOutlined /> }
                                    ]}
                                    value={onlineFilter}
                                    onChange={(val) => setOnlineFilter(val as string)}
                                />

                                {/* Game System Filter */}
                                <Select
                                    placeholder={t('invitations.browse.filter.systemPlaceholder', 'Filter by Game System')}
                                    value={selectedSystemId}
                                    onChange={setSelectedSystemId}
                                    allowClear
                                    loading={gamesLoading}
                                    style={{ minWidth: 240 }}
                                >
                                    {ttrpgGames?.map(game => (
                                        <Select.Option key={game.id} value={game.id}>
                                            <Flex align="center" gap="small">
                                                {game.mediaUrl && (
                                                    <Image src={game.mediaUrl} preview={false} width={18} height={18} style={{ borderRadius: 2, objectFit: 'cover' }} />
                                                )}
                                                <span>{game.title}</span>
                                            </Flex>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Flex>
                        </>
                    }
                />

                {/* INVITATIONS GRID */}
                {invitationsLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : invitations.length === 0 ? (
                    <Card style={{ ...launcherCard, textAlign: 'center', padding: '50px 20px' }}>
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <Title level={4}>{t('invitations.browse.empty.title', 'No Adventures Found')}</Title>
                                    <Paragraph type="secondary">
                                        {t('invitations.browse.empty.desc', 'No active published invitations match your selected filters right now. Try resetting filters.')}
                                    </Paragraph>
                                </div>
                            }
                        >
                            <Button type="primary" onClick={handleClearFilters}>
                                {t('invitations.browse.filter.clear', 'Clear Filters')}
                            </Button>
                        </Empty>
                    </Card>
                ) : (
                    <Row gutter={[24, 24]}>
                        {invitations.map((inv) => (
                            <Col xs={24} sm={12} md={8} key={inv.id}>
                                <InvitationSummaryCard item={inv} onSelect={() => navigate(`/invitations/${inv.id}`)} />
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </LauncherLayout>
    );
};

interface InvitationSummaryCardProps {
    item: PublicInvitationSummaryDto;
    onSelect: () => void;
}

const InvitationSummaryCard: React.FC<InvitationSummaryCardProps> = ({ item, onSelect }) => {
    const { t } = useTranslation();

    return (
        <Card
            hoverable
            onClick={onSelect}
            style={{
                ...launcherCard,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
            cover={
                <div style={{ height: '180px', overflow: 'hidden', position: 'relative', background: '#222' }}>
                    {item.coverUrl ? (
                        <img
                            alt={item.title}
                            src={item.coverUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <BookOutlined style={{ fontSize: '48px', color: '#666' }} />
                        </div>
                    )}
                    {/* TOP BADGES */}
                    <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {item.online ? (
                            <Tag color="cyan" icon={<GlobalOutlined />}>
                                {item.onlinePlatform || 'Online'}
                            </Tag>
                        ) : (
                            <Tag color="geekblue" icon={<EnvironmentOutlined />}>
                                {t('invitations.inPerson', 'In-Person')}
                            </Tag>
                        )}
                    </div>

                    <div style={{ position: 'absolute', top: 10, right: 10 }}>
                        <Tag color="green" icon={<TeamOutlined />}>
                            {item.availableSeats} {t('invitations.seatsLeft', 'seats left')}
                        </Tag>
                    </div>
                </div>
            }
            actions={[
                <Button type="primary" block icon={<ArrowRightOutlined />} onClick={onSelect} key="view" style={{ maxWidth: '95%' }}>
                    {t('invitations.viewDetails', 'View Details')}
                </Button>
            ]}
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Title level={4} style={{ marginTop: 0, marginBottom: 8 }} ellipsis={{ rows: 1 }}>
                    {item.title}
                </Title>

                {/* GAME SYSTEM */}
                <div style={{ marginBottom: 8 }}>
                    <Space size="small" align="center">
                        {item.systemMediaUrl && (
                            <img src={item.systemMediaUrl} alt="" style={{ width: 16, height: 16, borderRadius: 2 }} />
                        )}
                        <Text type="secondary" strong style={{ fontSize: 13 }}>
                            {item.systemTitle || t('organizer.adventures.unknown', 'Unknown System')}
                        </Text>
                    </Space>
                </div>

                {/* PLAY SPACE & GM */}
                <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
                    {item.playSpaceName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} className="text-ellipsis">
                            {item.online ? <GlobalOutlined style={{ color: '#13c2c2' }} /> : <EnvironmentOutlined style={{ color: '#2f54eb' }} />}
                            <Text type="secondary" ellipsis={{ tooltip: item.playSpaceName }}>
                                {item.playSpaceName}
                            </Text>
                        </div>
                    )}

                    {item.gmName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <UserOutlined style={{ color: '#722ed1' }} />
                            <Text type="secondary">
                                GM: {item.gmName}
                            </Text>
                        </div>
                    )}
                </div>

                {/* SESSIONS & DEADLINE & COST */}
                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', padding: '8px 12px', borderRadius: 6, marginTop: 'auto', marginBottom: 12, fontSize: 12 }}>
                    <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <span><CalendarOutlined /> {item.numberOfSessions} {t('invitations.sessionsCount', 'session(s)')}</span>
                        <span><DollarOutlined /> {item.costPerSession != null ? `${item.costPerSession} €` : 'Free'}</span>
                    </Flex>
                    <div style={{ color: '#888', fontSize: 11 }}>
                        {t('invitations.deadline', 'Deadline')}: {dayjs(item.invitationDeadline).format('YYYY-MM-DD HH:mm')}
                    </div>
                </div>

                {/* CONTENT WARNINGS */}
                {item.contentWarnings && item.contentWarnings.length > 0 && (
                    <div>
                        <Space wrap size={[0, 4]}>
                            {item.contentWarnings.map(cw => (
                                <Tag key={cw.id} color="orange" icon={<WarningOutlined />} style={{ fontSize: 11, margin: 2 }}>
                                    {t(cw.key || cw.id)}
                                </Tag>
                            ))}
                        </Space>
                    </div>
                )}
            </div>
        </Card>
    );
};
